/**
 * Estrutura e critérios de pontuação do MEEM, operacionalizados a partir de:
 *
 *   Brucki SMD, Nitrini R, Caramelli P, Bertolucci PHF, Okamoto IH.
 *   Sugestões para o uso do Mini-Exame do Estado Mental no Brasil.
 *   Arq Neuropsiquiatr. 2003;61(3-B):777-781.
 *   https://doi.org/10.1590/S0004-282X2003000500014
 *
 * O protocolo tem onze blocos de pontuação, e não sete. O agrupamento em sete
 * domínios reúne nomeação, repetição, comando, leitura e escrita sob
 * "linguagem"; os dois recortes são válidos, mas o de onze blocos é o do
 * instrumento e é o que serve para marcação item a item.
 *
 * Rótulos e critérios são redação própria descrevendo um procedimento
 * publicado. Esta não é a versão oficial nem licenciada do MMSE.
 */

/**
 * Dados normativos por bloco, n = 185 indivíduos saudáveis (Tabela 1 do artigo).
 * `minObservado` é o menor valor registrado no bloco entre esses indivíduos —
 * é o dado que mostra até onde uma pessoa sem alteração cognitiva pode cair.
 */
const norm = (media, dp, mediana, minObservado) => ({ media, dp, mediana, minObservado });

export const MEEM_BLOCKS = [
  {
    id: 'orientacaoTemporal',
    label: 'Orientação temporal',
    dominio: 'Orientação temporal',
    max: 5,
    normativo: norm(4.77, 0.51, 5, 3),
    nota:
      'Cinco perguntas independentes, um ponto cada. Nesta versão brasileira a referência final é a hora aproximada — item que substituiu o semestre, o qual por sua vez havia substituído a estação do ano do instrumento original.',
    items: [
      { label: 'Dia do mês', criterio: 'Informa o dia correto.' },
      { label: 'Mês', criterio: 'Nomeia o mês corrente.' },
      { label: 'Ano', criterio: 'Informa o ano corrente.' },
      { label: 'Dia da semana', criterio: 'Nomeia o dia da semana correto.' },
      {
        label: 'Hora aproximada',
        criterio: 'Único item do bloco com margem: erro de até uma hora, para mais ou para menos, ainda pontua.'
      }
    ]
  },
  {
    id: 'orientacaoEspacial',
    label: 'Orientação espacial',
    dominio: 'Orientação espacial',
    max: 5,
    normativo: norm(4.69, 0.6, 5, 2),
    nota:
      'Cinco níveis, do mais específico ao mais amplo. Os dois primeiros eram "andar" e "nome do hospital" nas versões anteriores e foram generalizados para que o teste sirva também a consultório e a domicílio.',
    items: [
      {
        label: 'Local específico',
        criterio: 'Identifica o tipo de cômodo em que está — consultório, dormitório, sala. Pergunta-se apontando para o chão.'
      },
      {
        label: 'Local geral',
        criterio: 'Identifica o tipo de estabelecimento — hospital, casa de repouso, residência. Pergunta-se apontando ao redor, em sentido amplo. Não é o nome do lugar, é a natureza dele.'
      },
      {
        label: 'Bairro ou rua próxima',
        criterio: 'Qualquer uma das duas respostas pontua.'
      },
      { label: 'Cidade', criterio: 'Nomeia a cidade corretamente.' },
      { label: 'Estado', criterio: 'Nomeia o estado corretamente.' }
    ]
  },
  {
    id: 'memoriaImediata',
    label: 'Memória imediata',
    dominio: 'Registro',
    max: 3,
    normativo: norm(2.99, 0.07, 3, 2),
    nota:
      'Três palavras sem relação entre si são apresentadas, conforme a versão validada utilizada. Pontua apenas o que a pessoa repete na PRIMEIRA apresentação. O examinador pode repetir a lista até três vezes se houver erro, mas essas repetições existem para viabilizar a evocação mais adiante e não recuperam pontuação. Este é o erro de aplicação mais comum do teste.',
    items: [
      { label: '1ª palavra repetida', criterio: 'Ponto pela repetição correta na primeira tentativa.' },
      { label: '2ª palavra repetida', criterio: 'Ponto pela repetição correta na primeira tentativa.' },
      { label: '3ª palavra repetida', criterio: 'Ponto pela repetição correta na primeira tentativa.' }
    ]
  },
  {
    id: 'calculo',
    label: 'Cálculo',
    dominio: 'Atenção e cálculo',
    max: 5,
    normativo: norm(2.65, 1.77, 3, 0),
    nota:
      'Subtração seriada de sete a partir de 100. Diante de um erro, o examinador corrige e a série continua: as etapas seguintes são conferidas a partir do valor corrigido, não do valor que a pessoa disse. Autocorreção espontânea conta como acerto. Esta versão não prevê a soletração invertida como alternativa — ela foi descartada por ser difícil demais em baixa escolaridade e por soletrar não ser hábito corrente no Brasil, enquanto muitas pessoas sem escolaridade formal calculam.',
    items: [
      { label: '100 − 7', criterio: 'Resultado esperado: 93.' },
      { label: '93 − 7', criterio: 'Resultado esperado: 86.' },
      { label: '86 − 7', criterio: 'Resultado esperado: 79.' },
      { label: '79 − 7', criterio: 'Resultado esperado: 72.' },
      { label: '72 − 7', criterio: 'Resultado esperado: 65.' }
    ]
  },
  {
    id: 'evocacao',
    label: 'Evocação',
    dominio: 'Evocação',
    max: 3,
    normativo: norm(2.09, 0.88, 2, 0),
    nota: 'Pergunta-se quais eram as três palavras apresentadas no bloco de memória imediata. Um ponto por palavra recuperada.',
    items: [
      { label: '1ª palavra evocada', criterio: 'Ponto pela palavra recuperada espontaneamente.' },
      { label: '2ª palavra evocada', criterio: 'Ponto pela palavra recuperada espontaneamente.' },
      { label: '3ª palavra evocada', criterio: 'Ponto pela palavra recuperada espontaneamente.' }
    ]
  },
  {
    id: 'nomeacao',
    label: 'Nomeação',
    dominio: 'Linguagem',
    max: 2,
    normativo: norm(1.99, 0.1, 2, 1),
    nota: 'Dois objetos comuns são mostrados fisicamente à pessoa, um de cada vez.',
    items: [
      { label: 'Nomeou o 1º objeto', criterio: 'Ponto pela nomeação correta do objeto apresentado.' },
      { label: 'Nomeou o 2º objeto', criterio: 'Ponto pela nomeação correta do objeto apresentado.' }
    ]
  },
  {
    id: 'repeticao',
    label: 'Repetição',
    dominio: 'Linguagem',
    max: 1,
    normativo: norm(0.94, 0.24, 1, 0),
    nota: 'Uma frase curta, sem sentido óbvio, é lida uma única vez para a pessoa repetir — conforme a versão validada utilizada.',
    items: [
      {
        label: 'Repetiu a frase',
        criterio: 'Item de tudo ou nada: só pontua se a repetição for perfeita. Não há pontuação parcial.'
      }
    ]
  },
  {
    id: 'comando',
    label: 'Comando de três etapas',
    dominio: 'Linguagem',
    max: 3,
    normativo: norm(2.85, 0.37, 3, 1),
    nota:
      'Um comando com três ações sucessivas é emitido uma única vez, inteiro, conforme a versão validada utilizada. Se a pessoa pedir ajuda durante a execução, o examinador não dá dicas: observa e pontua o que foi feito. A pontuação é por etapa, não pelo comando como um todo.',
    items: [
      { label: '1ª etapa executada', criterio: 'Ponto pela etapa executada corretamente.' },
      { label: '2ª etapa executada', criterio: 'Ponto pela etapa executada corretamente.' },
      { label: '3ª etapa executada', criterio: 'Ponto pela etapa executada corretamente.' }
    ]
  },
  {
    id: 'leitura',
    label: 'Leitura com execução',
    dominio: 'Linguagem',
    max: 1,
    normativo: norm(0.85, 0.36, 1, 0),
    nota: 'Uma ordem curta, escrita em letras grandes conforme a versão validada utilizada, é apresentada para a pessoa ler e executar o que está escrito. O examinador não auxilia, nem se a pessoa pedir.',
    items: [
      {
        label: 'Leu e executou a ordem escrita',
        criterio: 'Pontua a execução da ação pedida. Ler a frase em voz alta sem executar não pontua.'
      }
    ]
  },
  {
    id: 'frase',
    label: 'Escrita de frase',
    dominio: 'Linguagem',
    max: 1,
    normativo: norm(0.71, 0.45, 1, 0),
    nota:
      'Se a pessoa não compreender o pedido, o examinador pode sugerir que escreva algo com começo, meio e fim, algo que tenha acontecido no dia, ou algo que ela queira dizer.',
    items: [
      {
        label: 'Escreveu uma frase com sentido',
        criterio: 'Avalia-se a produção de uma frase com sentido. Erros de gramática e de ortografia não descontam ponto.'
      }
    ]
  },
  {
    id: 'desenho',
    label: 'Cópia do desenho',
    dominio: 'Capacidade visuoconstrutiva',
    figura: true,
    max: 1,
    normativo: norm(0.65, 0.48, 1, 0),
    nota:
      'Mostra-se o modelo e pede-se a melhor reprodução possível. É o item com o critério mais específico do teste e o de menor média na amostra normativa.',
    items: [
      {
        label: 'Copiou a figura',
        criterio:
          'Pontua se estiverem presentes dois pentágonos intersecionados, totalizando dez ângulos, com a interseção formando uma figura de quatro lados, ou com dois ângulos.'
      }
    ]
  }
];

export const MEEM_TOTAL_MAX = MEEM_BLOCKS.reduce((acc, b) => acc + b.max, 0);

/** Agrupamento em sete domínios, mantido para a leitura por domínio. */
export const MEEM_DOMAIN_TOTALS = MEEM_BLOCKS.reduce((acc, b) => {
  acc[b.dominio] = (acc[b.dominio] || 0) + b.max;
  return acc;
}, {});

/**
 * Blocos em que perder ponto é comum entre pessoas SEM alteração cognitiva.
 *
 * Critério: média normativa abaixo de 90% do máximo do bloco. Não é um
 * julgamento editorial item a item — é derivado da Tabela 1 do artigo, para
 * que a regra fique auditável e mude junto se os dados mudarem.
 */
export const LIMIAR_PERDA_COMUM = 0.9;

export function isPerdaComum(block) {
  return block.normativo.media / block.max < LIMIAR_PERDA_COMUM;
}

export function blocosDePerdaComum() {
  return MEEM_BLOCKS.filter(isPerdaComum);
}

const plural = (n, singular, pluralForma) => `${n} ${n === 1 ? singular : pluralForma}`;

/**
 * Leitura de um bloco, como texto explícito.
 *
 * Fica aqui, e não dentro do componente, por dois motivos: é testável, e a
 * frase que o profissional lê passa a ser resultado de uma regra e não de uma
 * ramificação escondida no JSX.
 *
 * A distinção que a função existe para tornar explícita: perder ponto e ficar
 * abaixo do esperado NÃO são a mesma coisa. O máximo é o teto do instrumento;
 * a mediana é o que pessoas saudáveis de fato pontuaram naquele bloco. No
 * cálculo, por exemplo, a mediana é 3 de 5 — quem acerta 3 perdeu 2 pontos e
 * ainda assim pontuou como a metade superior da amostra normativa.
 */
export function leituraDoBloco(block, obtido) {
  const { max, normativo } = block;
  const perdidos = max - obtido;
  const abaixoDaMediana = obtido < normativo.mediana;
  const perdaComum = isPerdaComum(block);

  if (perdidos === 0) {
    return {
      perdidos,
      situacao: 'sem-perda',
      texto: 'Sem perda neste bloco.'
    };
  }

  if (!abaixoDaMediana) {
    return {
      perdidos,
      situacao: 'perda-dentro-do-esperado',
      texto:
        `Perdeu ${plural(perdidos, 'ponto', 'pontos')}, mas ficou igual ou acima da ` +
        `mediana de ${normativo.mediana} observada em pessoas saudáveis. Perder ponto ` +
        'aqui não significa ficar abaixo do esperado: o máximo é o teto do instrumento, ' +
        'a mediana é o que a amostra normativa de fato pontuou.'
    };
  }

  if (perdaComum) {
    return {
      perdidos,
      situacao: 'abaixo-em-bloco-de-perda-frequente',
      texto:
        `Perdeu ${plural(perdidos, 'ponto', 'pontos')} e ficou abaixo da mediana de ` +
        `${normativo.mediana}. É um bloco em que a perda é frequente sem alteração ` +
        `cognitiva: a média entre pessoas saudáveis foi ${String(normativo.media).replace('.', ',')} ` +
        `de ${max}, e houve quem pontuasse ${normativo.minObservado} na amostra.`
    };
  }

  return {
    perdidos,
    situacao: 'abaixo-em-bloco-habitualmente-integro',
    texto:
      `Perdeu ${plural(perdidos, 'ponto', 'pontos')} e ficou abaixo da mediana de ` +
      `${normativo.mediana}. É um bloco em que pessoas saudáveis costumam acertar quase ` +
      `tudo: a média foi ${String(normativo.media).replace('.', ',')} de ${max}, e o menor ` +
      `valor registrado na amostra foi ${normativo.minObservado}.`
  };
}
